/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { Cohort, InitialScrsTable, NumericTable } from "services/computations/types"

import { ALL_COHORT, filterCohorts } from "./cohorts"
import { CONSUMPTION_RATE_DECIMALS } from "./computeResults"
import { computeConsumptionRatesForSelectedCohorts } from "./utils"

export function computeConsumptionRates(
  table: InitialScrsTable,
  failureRates: readonly number[],
  cohort?: Cohort | null
): NumericTable {
  // 1. Default to an "all data" cohort if none supplied
  const activeCohort = cohort ?? ALL_COHORT

  // 2. Filter the DataFrame according to the cohort's year, drawdown, and CAPE criteria
  const filteredInitialScrs = filterCohorts(table, activeCohort)

  // 3. For each failure rate, compute the corresponding quantile of the
  //    initial_scr column and return as a single‑row DataFrame
  return computeConsumptionRatesForSelectedCohorts(filteredInitialScrs, failureRates)
}

export function makeConsumptionRateRange(
  initialScrsTable: InitialScrsTable,
  userInitialConsumptionRate?: number | null
): number[] {
  // 1. Re-run at 0% failure to get the "failsafe" base rate
  const consumptionRates = computeConsumptionRates(initialScrsTable, [0.0], null)
  const allTimeFailsafeConsumptionRate = consumptionRates["0.00"][0]

  // 2. "Floor" it to the nearest 0.0025, but never below 0 - negative
  //    consumption rates aren't meaningful for failure-rate calculations.
  const lowest = Math.max(0, Math.round(allTimeFailsafeConsumptionRate * 400 - 0.5) / 400)

  // 3. Build the baseline rates in 0.0025 increments for a 0.0225 span
  const baseRates: number[] = []
  for (let rate = lowest; rate < lowest + 0.0225; rate += 0.0025) {
    baseRates.push(rate) // Don't round here
  }

  // 4. If the user supplied an initial rate, merge it in
  if (userInitialConsumptionRate != null) {
    // Concatenate arrays
    const allRates = [...baseRates, userInitialConsumptionRate]

    const roundedRates = allRates.map(r => Number(r.toFixed(CONSUMPTION_RATE_DECIMALS)))

    // Deduplicate, drop any negative rate (userInitialConsumptionRate isn't
    // validated as non-negative upstream), and sort
    return Array.from(new Set(roundedRates))
      .filter(r => r >= 0)
      .sort((a, b) => a - b)
  }

  // 5. Otherwise return just the baseline sequence
  return baseRates
}
